/**
 * Scoring recommendation types shared between frontend and backend.
 * Single source of truth for recommendation values from the scoring engine.
 */

import {
  DEFAULT_RAW_THRESHOLDS,
  getDisplayRecommendation,
  normalizeScoreForDisplay,
} from './scoreDisplay';

/**
 * Raw scoring engine output values.
 * Stored in pick_predictions table (strategy_recommendation, prediction_recommendation).
 */
export const SCORING_RECOMMENDATIONS = ['BET', 'LEAN_BET', 'SKIP', 'CAUTION'] as const;
export type ScoringRecommendation = (typeof SCORING_RECOMMENDATIONS)[number];

/**
 * Display-friendly recommendation values.
 * Stored in parlays table (tracked_strategy_rec, tracked_prediction_rec).
 * CAUTION is now a distinct display category (no longer merged with LEAN BET).
 */
export const DISPLAY_RECOMMENDATIONS = ['BET', 'LEAN BET', 'CAUTION', 'SKIP'] as const;
export type DisplayRecommendation = (typeof DISPLAY_RECOMMENDATIONS)[number];

/** Convert raw scoring recommendation to display form */
export function toDisplayRecommendation(raw: ScoringRecommendation): DisplayRecommendation {
  switch (raw) {
    case 'BET':
      return 'BET';
    case 'LEAN_BET':
      return 'LEAN BET';
    case 'CAUTION':
      return 'CAUTION';
    case 'SKIP':
      return 'SKIP';
  }
}

/**
 * Internal scoring engine recommendation (lowercase).
 * Used in scoring engines and calibratedScoring.ts during calculation.
 */
export type InternalRecommendation = 'bet' | 'skip' | 'caution';

/** Check if a value is a valid ScoringRecommendation */
export function isValidScoringRecommendation(value: unknown): value is ScoringRecommendation {
  return (
    typeof value === 'string' && (SCORING_RECOMMENDATIONS as readonly string[]).includes(value)
  );
}

/**
 * Derive the stored recommendation from a raw score and thresholds.
 *
 * The scoring engine only produces 3 values ('bet' | 'caution' | 'skip'),
 * but display/storage needs 4 tiers (BET | LEAN BET | CAUTION | SKIP).
 * The 4th tier (LEAN BET) is determined by normalizing the raw score
 * to the 0-100 display scale and applying display thresholds.
 *
 * Use this everywhere a recommendation is stored in the DB.
 */
export function deriveStoredRecommendation(
  rawScore: number,
  thresholds?: { bet: number; skip: number },
): DisplayRecommendation {
  const displayScore = normalizeScoreForDisplay(rawScore, thresholds ?? DEFAULT_RAW_THRESHOLDS);
  return getDisplayRecommendation(displayScore) as DisplayRecommendation;
}
